import { z } from 'zod';
import { ModuleTypeEnum } from './module-type';
import { 
  TaskModuleSettingsSchema, 
  ProjectModuleSettingsSchema, 
  TimeEntryModuleSettingsSchema 
} from './organization-settings';

// Project-specific settings (individual project overrides)
export const ProjectSpecificSettingsSchema = z.object({
  // Override organization defaults
  requireProjectCode: z.boolean().optional(),
  projectCodePrefix: z.string().optional(),
  enableTimelines: z.boolean().optional(),
  defaultProjectStatus: z.string().optional(),
  
  // Project-specific settings
  customFields: z.record(z.string(), z.unknown()).optional(),
  accessLevel: z.enum(['PUBLIC', 'PRIVATE', 'RESTRICTED']).default('PUBLIC'),
  allowGuestAccess: z.boolean().default(false),
  maxTasksPerSprint: z.number().optional(),
  budgetLimit: z.number().optional(),
  
  // Workflow settings
  taskApprovalRequired: z.boolean().default(false),
  timeEntryApprovalRequired: z.boolean().default(false),
  allowFileAttachments: z.boolean().default(true),
  maxFileSize: z.number().default(10), // MB
});

export type ProjectSpecificSettings = z.infer<typeof ProjectSpecificSettingsSchema>;

// Entity-specific settings  
export const EntitySpecificSettingsSchema = z.object({
  // Billing and time tracking
  defaultBillableRate: z.number().optional(),
  requireTimeApproval: z.boolean().default(false),
  roundingMethod: z.enum(['UP', 'DOWN', 'NEAREST']).default('NEAREST'),
  minimumBillableTime: z.number().default(0), // minutes
  
  // Access and permissions
  allowDirectContact: z.boolean().default(false),
  portalAccess: z.boolean().default(false),
  allowDocumentSharing: z.boolean().default(true),
  
  // Entity-specific workflow
  customApprovalWorkflow: z.boolean().default(false),
  requireNDA: z.boolean().default(false),
  preferredCommunicationMethod: z.enum(['EMAIL', 'PHONE', 'SLACK', 'TEAMS']).optional(),
  
  // Invoicing settings
  invoiceFrequency: z.enum(['WEEKLY', 'MONTHLY', 'PROJECT_END']).default('MONTHLY'),
  paymentTerms: z.number().default(30), // days
  currency: z.string().optional(), // Override org currency
});

export type EntitySpecificSettings = z.infer<typeof EntitySpecificSettingsSchema>;

// Task-specific settings
export const TaskSpecificSettingsSchema = z.object({
  // Override project/org defaults
  allowRecurring: z.boolean().optional(),
  requireDueDate: z.boolean().optional(),
  defaultPriority: z.string().optional(),
  
  // Task-specific
  allowSubtasks: z.boolean().default(true),
  maxSubtaskDepth: z.number().default(3),
  allowTimeTracking: z.boolean().default(true),
  allowComments: z.boolean().default(true),
  
  // Approval workflow
  requireApproval: z.boolean().default(false),
  approverIds: z.array(z.string()).optional(),
  
  // Notifications
  notifyOnStatusChange: z.boolean().default(true),
  notifyOnDueDateApproach: z.boolean().default(true),
  dueDateReminderDays: z.number().default(1),
});

export type TaskSpecificSettings = z.infer<typeof TaskSpecificSettingsSchema>;

// Generic module settings container
export interface ModuleSpecificSettings {
  [ModuleTypeEnum.PROJECT]?: ProjectSpecificSettings;
  [ModuleTypeEnum.ENTITY]?: EntitySpecificSettings;
  [ModuleTypeEnum.TASK]?: TaskSpecificSettings;
  // Add more as needed
}

// Settings inheritance utility
export interface SettingsInheritance<T extends Record<string, any>> {
  organizationDefault: T;
  moduleSpecific?: Partial<T>;
  userOverride?: Partial<T>;
}

export function resolveModuleSettings<T extends Record<string, any>>(
  inheritance: SettingsInheritance<T>
): T {
  return {
    ...inheritance.organizationDefault,
    ...inheritance.moduleSpecific,
    ...inheritance.userOverride,
  } as T;
}

// Preference keys for module-specific settings
export const MODULE_SETTINGS_KEYS = {
  PROJECT_SPECIFIC: (projectId: string) => `module.project.${projectId}.settings`,
  ENTITY_SPECIFIC: (entityId: string) => `module.entity.${entityId}.settings`, 
  TASK_SPECIFIC: (taskId: string) => `module.task.${taskId}.settings`,
} as const;

// Helper to get effective settings for a module
export function getEffectiveModuleSettings<T extends Record<string, any>>(
  moduleType: ModuleTypeEnum,
  moduleId: string,
  organizationDefaults: T,
  moduleOverrides?: Partial<T>,
  userOverrides?: Partial<T>
): T {
  return resolveModuleSettings({
    organizationDefault: organizationDefaults,
    moduleSpecific: moduleOverrides,
    userOverride: userOverrides,
  });
}

// Default factory functions
export function getDefaultProjectSettings(): ProjectSpecificSettings {
  return ProjectSpecificSettingsSchema.parse({});
}

export function getDefaultEntitySettings(): EntitySpecificSettings {
  return EntitySpecificSettingsSchema.parse({});
}

export function getDefaultTaskSettings(): TaskSpecificSettings {
  return TaskSpecificSettingsSchema.parse({});
} 